import { validateWizardImplementations } from './create-wizard';
import {
  effectiveSteps,
  initialWizardState,
  resolveBackTarget,
  transitionWizard,
} from './transition';
import {
  BACK_EVENT,
  GOTO_EVENT,
  NEXT_EVENT,
  RECONCILE_EVENT,
  RESET_EVENT,
  SKIP_EVENT,
  type Wizard,
  type WizardEvent,
  type WizardImplementations,
  type WizardPersistedSnapshot,
  type WizardSnapshot,
  type WizardState,
} from './types';

export type WizardActor<TStep extends string> = {
  start: () => WizardActor<TStep>;
  stop: () => void;
  /**
   * Navigation actions return whether the transition was **taken** — `false`
   * on refused navigation (failed `validate`, non-skippable `skip`, nowhere
   * to go back to, disabled `goTo` target), before `start()`, and once
   * `done`. Consumers doing per-navigation side effects (analytics with call
   * metadata) should act only on `true` — never fire for a refused move. A
   * re-entrant call from a subscriber / `onTransition` callback is queued and
   * reports `false` (deferred processing).
   */
  next: () => boolean;
  back: () => boolean;
  skip: () => boolean;
  goTo: (step: TStep) => boolean;
  /**
   * Return to the initial position (the `input.step` seed or first declared
   * step) with completion and history cleared — "start over" for a scope that
   * outlives its flow (e.g. a modal that closes and reopens). Emits a `RESET`
   * record; does not re-run a persistence restore.
   */
  reset: () => void;
  /**
   * Re-evaluate the `enabled` guards. Reconciles (with a transition record)
   * when the current step fell out of the effective list; otherwise just
   * refreshes the snapshot's totals. `useWizard` calls this after every commit,
   * so React consumers never need to.
   */
  refresh: () => void;
  getSnapshot: () => WizardSnapshot<TStep>;
  /** The pre-start snapshot — used as the SSR/server snapshot by `useWizard`. */
  getInitialSnapshot: () => WizardSnapshot<TStep>;
  subscribe: (listener: (snapshot: WizardSnapshot<TStep>) => void) => () => void;
};

function rethrowAsync(error: unknown): void {
  queueMicrotask(() => {
    throw error;
  });
}

/**
 * Interpret a wizard into a running actor. Owns snapshot derivation,
 * transition records, list reconciliation, and the persistence contract
 * (`save` after every taken transition, `restore` once on start, async restore
 * with `isHydrating` + discard-after-navigation). `start`/`stop` are
 * idempotent (safe under React StrictMode double-mount).
 *
 * Fail-fast: `start()` validates every guard name in the config against
 * `impls` and throws listing what's missing. Creating an actor without impls
 * is fine for snapshot-only use (SSR) — the initial snapshot is the first
 * *declared* step and evaluates no guards.
 */
export function interpretWizard<TStep extends string>(
  wizard: Wizard<TStep>,
  impls: WizardImplementations<TStep> = {}
): WizardActor<TStep> {
  let state: WizardState<TStep> = initialWizardState(wizard, impls.input?.step);
  let started = false;
  let isHydrating = false;
  let navigated = false;
  let restoreAttempted = false;
  const listeners = new Set<(snapshot: WizardSnapshot<TStep>) => void>();

  function buildSnapshot(list: readonly TStep[]): WizardSnapshot<TStep> {
    const index = list.indexOf(state.step);
    const totalSteps = list.length;
    const stepNumber = state.done ? totalSteps : index === -1 ? 1 : index + 1;
    const completed = state.completed;
    return {
      step: state.step,
      steps: list,
      stepNumber,
      totalSteps,
      progress: state.done ? 1 : totalSteps === 0 ? 0 : stepNumber / totalSteps,
      canGoBack: !state.done && resolveBackTarget(wizard, state, list) !== null,
      done: state.done,
      isHydrating,
      isCompleted: (step: TStep) => completed.has(step),
    };
  }

  // The pre-start snapshot deliberately evaluates no guards (SSR-safe): the
  // effective list is taken as the full declared order.
  const snapshot0 = buildSnapshot(wizard.stepIds);
  let snapshot = snapshot0;

  function notify(): void {
    for (const listener of listeners) listener(snapshot);
  }

  // Identity of the completed set behind the currently published snapshot —
  // lets refreshSnapshot detect completion changes without comparing the
  // (always-fresh) `isCompleted` closure.
  let lastCompleted = state.completed;

  /** Rebuild the snapshot; notify only when something observable changed. */
  function refreshSnapshot(): void {
    const next = buildSnapshot(effectiveSteps(wizard, impls.guards));
    const sameList =
      next.steps.length === snapshot.steps.length &&
      next.steps.every((step, i) => step === snapshot.steps[i]);
    const changed =
      next.step !== snapshot.step ||
      next.stepNumber !== snapshot.stepNumber ||
      !sameList ||
      next.canGoBack !== snapshot.canGoBack ||
      next.done !== snapshot.done ||
      next.isHydrating !== snapshot.isHydrating ||
      state.completed !== lastCompleted;
    if (!changed) return;
    lastCompleted = state.completed;
    snapshot = next;
    notify();
  }

  function persist(): void {
    const adapter = impls.persistence;
    if (!adapter?.save) return;
    const persisted: WizardPersistedSnapshot<TStep> = {
      step: state.step,
      completed: [...state.completed],
      history: [...state.history],
      done: state.done,
    };
    try {
      const result = adapter.save(persisted);
      if (result instanceof Promise) result.catch(rethrowAsync);
    } catch (error) {
      rethrowAsync(error);
    }
  }

  type QueueItem = {
    event: WizardEvent<TStep>;
    /** Written by `process` so a synchronous `navigate` can report taken/refused. */
    outcome?: { taken: boolean };
  };
  const queue: QueueItem[] = [];
  let processing = false;

  function process(item: QueueItem): void {
    const next = transitionWizard(wizard, state, item.event, {
      guards: impls.guards,
    });
    if (next === state) return; // refused / no-op: no record, no save
    const from = state.step;
    state = next;
    if (item.outcome) item.outcome.taken = true;
    lastCompleted = state.completed;
    snapshot = buildSnapshot(effectiveSteps(wizard, impls.guards));
    notify();
    impls.onTransition?.({
      from,
      event: item.event.type,
      to: state.step,
      stepNumber: snapshot.stepNumber,
      totalSteps: snapshot.totalSteps,
    });
    persist();
  }

  function drain(): void {
    if (processing) return;
    processing = true;
    let completedDrain = false;
    try {
      while (queue.length) {
        const item = queue.shift() as QueueItem;
        process(item);
      }
      completedDrain = true;
    } finally {
      processing = false;
      // An exception mid-drain leaves the wizard indeterminate — drop the
      // unprocessed items rather than replaying them on a later dispatch.
      if (!completedDrain) queue.length = 0;
    }
  }

  function dispatch(event: WizardEvent<TStep>): void {
    if (!started) return;
    queue.push({ event });
    drain();
  }

  function reconcileIfNeeded(): void {
    if (state.done) return;
    const list = effectiveSteps(wizard, impls.guards);
    if (!list.includes(state.step)) dispatch({ type: RECONCILE_EVENT });
  }

  function applyRestore(persisted: WizardPersistedSnapshot<TStep> | null): void {
    if (persisted === null) return;
    // Stale external data (e.g. a persisted step id that no longer exists) is
    // discarded, not thrown — unlike config typos, it isn't a programmer error.
    if (!wizard.stepIds.includes(persisted.step)) return;
    const declared = (step: TStep) => wizard.stepIds.includes(step);
    state = {
      step: persisted.step,
      completed: new Set(persisted.completed.filter(declared)),
      history: persisted.history.filter(declared),
      done: persisted.done === true,
    };
    refreshSnapshot();
    // A restored step whose `enabled` guard now fails goes through the normal
    // reconciliation (which emits a record and re-saves), never entered verbatim.
    reconcileIfNeeded();
  }

  function attemptRestore(): void {
    const adapter = impls.persistence;
    if (!adapter || restoreAttempted) return;
    restoreAttempted = true;
    let result: ReturnType<typeof adapter.restore>;
    try {
      result = adapter.restore();
    } catch (error) {
      rethrowAsync(error);
      return;
    }
    if (result instanceof Promise) {
      isHydrating = true;
      refreshSnapshot();
      result.then(
        (persisted) => {
          isHydrating = false;
          // A restore that lands after the user navigated (or after stop) is
          // discarded — the user's position wins.
          if (started && !navigated) applyRestore(persisted);
          else refreshSnapshot();
        },
        (error) => {
          isHydrating = false;
          refreshSnapshot();
          rethrowAsync(error);
        }
      );
      return;
    }
    applyRestore(result);
  }

  function navigate(event: WizardEvent<TStep>): boolean {
    if (!started) return false;
    navigated = true;
    const outcome = { taken: false };
    queue.push({ event, outcome });
    drain();
    // Re-entrant calls (from a subscriber mid-drain) are still queued at this
    // point — they process when the outer drain reaches them and report false.
    return outcome.taken;
  }

  const actor: WizardActor<TStep> = {
    start() {
      if (started) return actor;
      validateWizardImplementations(wizard, impls);
      started = true;
      // The initial state is the first *declared* step — reconcile immediately
      // if its `enabled` guard fails, then hand position over to restore.
      refreshSnapshot();
      reconcileIfNeeded();
      attemptRestore();
      return actor;
    },
    stop() {
      if (!started) return;
      started = false;
      queue.length = 0;
    },
    next: () => navigate({ type: NEXT_EVENT }),
    back: () => navigate({ type: BACK_EVENT }),
    skip: () => navigate({ type: SKIP_EVENT }),
    goTo: (step) => navigate({ type: GOTO_EVENT, step }),
    reset() {
      if (!started) return;
      const from = state.step;
      state = initialWizardState(wizard, impls.input?.step);
      lastCompleted = state.completed;
      snapshot = buildSnapshot(effectiveSteps(wizard, impls.guards));
      notify();
      impls.onTransition?.({
        from,
        event: RESET_EVENT,
        to: state.step,
        stepNumber: snapshot.stepNumber,
        totalSteps: snapshot.totalSteps,
      });
      persist();
      // A seeded step whose guard fails right now still reconciles normally.
      reconcileIfNeeded();
    },
    refresh() {
      if (!started) return;
      reconcileIfNeeded();
      refreshSnapshot();
    },
    getSnapshot: () => snapshot,
    getInitialSnapshot: () => snapshot0,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
  return actor;
}
